/**
 * Die Gegenüberstellung: die eigenen Befunde neben denen der Agenten (Spec, User
 * Stories 83 bis 85).
 *
 * Vier Festlegungen tragen sie:
 *
 * - **Vor der Abgabe steht hier nichts, und die Ansicht sagt warum.** Dass
 *   tatsächlich nichts kommt, setzt der Server durch — er liefert die fremden
 *   Befunde gar nicht erst aus (ADR-0007). Diese Datei zeigt nur, was sie
 *   bekommt; ein geschlossener Modus, den allein sie einhielte, wäre keiner.
 *   **Zurückgehalten ist allein diese Fläche** und nicht mehr der Befund: Sie
 *   *stellt gegenüber*, und vor der Abgabe gäbe es nichts zu vergleichen.
 * - **Übereinstimmung und Abweichung sind unterscheidbar.** Eine Zuordnung mit
 *   zwei Seiten ist Übereinstimmung; eine mit leerer Gegenseite ist Abweichung,
 *   und die Leerstelle steht sichtbar da statt zu fehlen.
 * - **Der Treffer hängt allein am Ort.** Ob Befundart und Schweregrad
 *   übereinstimmten, steht daneben als eigene Aussage — es geht nicht in den
 *   Treffer ein und darf deshalb auch nicht so aussehen.
 * - **Bewertet wird die Stelle, mit demselben Handgriff wie in der
 *   Detailansicht** (ADR-0022). Der alte Weg je Befund ist weg: Er setzte allein
 *   die Qualifizierung, und er setzte sie am einzelnen Melder — eine Zeile, die
 *   nach dem Klick zur Hälfte unbeurteilt bliebe, ist nicht das, was dort steht.
 *   Zugeordnet wird weiter über **Überlappung** (`FindingMatcher`, ADR-0015);
 *   der Handgriff greift trotzdem am **exakten Anker**, und wo eine Zuordnung
 *   mehrere Anker umfasst, steht er je Anker. Eine Gruppe ist keine Stelle.
 *
 * **Die Kontrolle steht als dritte Spalte daneben** (ADR-0010). Sie wird nicht
 * gegen den Menschen gemessen: Ihre Zählungen sind eine Deckungszahl und keine
 * Abweichung, und sie stehen außerhalb jeder Zahl je Agent — ein Kontrollbefund
 * gehört zu keinem. Was kein Muster deckt, steht dort gar nicht; die Kontrolle
 * behauptet außerhalb ihrer Muster nichts.
 *
 * Der Nachvollzug — der Modellaufruf hinter einem Befund eines Agenten — steht
 * nur Administratoren offen. Sichtbar ist er hier, weil hier die seltsame Aussage
 * steht, der jemand nachgeht; entschieden wird der Zugriff im Server. Am
 * Kontrollbefund gibt es ihn nicht: Er entsteht ohne Modellaufruf.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Chip, Dialog, Modal, StatusPill } from "../../design-system";
import { ForeignFixError } from "../../api/client";
import type {
  ComparisonAgent,
  ComparisonApi,
  ControlComparison,
  Finding,
  FindingMatch,
  FollowUpTally,
  ModelCall,
  ModelCallApi,
  Role,
  SiteAnchor,
  Verdict,
} from "../../api/client";
import { formatNumber, formatShare, formatTimestamp } from "../../formatting";
import {
  labelOfJudgement,
  QUALIFICATION_LABELS,
  QUALIFICATION_TONES,
  SEVERITY_TONES,
  STATUS_LABELS,
  STATUS_TONES,
} from "../findings";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";
import { anchorOfFinding, describeAnchor, siteAnchorOf, siteKeyOf } from "./anchor";
import { FollowUp, FollowUpDialog, followUpOf } from "./followUp";
import { OverwriteDialog, VerdictBar } from "./verdict";
import type { Overwrite } from "./verdict";

/**
 * Was vor der Abgabe an der Stelle der Gegenüberstellung steht.
 *
 * Er hat einmal versprochen, die Befunde der Agenten blieben bis dahin
 * zurückgehalten. Das stimmt seit ADR-0013 nur noch für **diese Fläche** und
 * nicht mehr für die Befunde: Die stehen ohne Vorbedingung da, und seit
 * ADR-0022 lassen sie sich ohne Vorbedingung auch bewerten. Der Satz sagt
 * deshalb, was hier wartet und warum — und wo die Befunde stattdessen zu
 * finden sind.
 */
const CLOSED =
  "Die Gegenüberstellung erscheint nach Ihrer Abgabe — vorher gäbe es nichts zu " +
  "vergleichen. Die Befunde selbst warten nicht auf sie: Sie stehen oben in dieser " +
  "Ansicht und im Befundvorrat, und bewerten lassen sie sich dort ebenso.";

const NOTHING =
  "Kein Agent hat diese Revision begutachtet. Es gibt nichts gegenüberzustellen.";

const AGREED = "Beide Seiten haben nichts gefunden.";

const NO_CONTROL =
  "Die Kontrolle hat hier nichts gefunden. Sie urteilt nicht — sie stellt fest, was ihre " +
  "Muster decken, und schweigt zum Rest.";

const FOLDED =
  "Ihre eigenen Befunde neben denen der Agenten, samt Abweichungskennzahl. Sie steht " +
  "erst nach Ihrer Abgabe — vorher gäbe es nichts zu vergleichen. Für den Blick auf " +
  "die Befunde selbst brauchen Sie sie nicht: Die stehen oben, vollständig und ohne " +
  "Vorbedingung.";

/**
 * Die Gegenüberstellung — **eingeklappt**, bis jemand sie aufklappt.
 *
 * Sie ist ein Nebenprodukt für den, der es sucht, und war nie die Voraussetzung
 * dafür, Befunde zu sehen. Aufgeklappt hätte sie über dem Befundverlauf den
 * Platz belegt und vor der Abgabe nichts als einen Hinweis darauf gezeigt, dass
 * hier noch nichts steht — der Leser hätte dreimal gelesen, was er nicht
 * bekommt, bevor er sah, was er bekommt.
 *
 * Der Inhalt liegt in einer eigenen Komponente, die erst beim Aufklappen
 * entsteht: So wird auch erst dann geladen. Eine Abfrage für einen Kasten, den
 * niemand geöffnet hat, ist eine Abfrage zu viel.
 *
 * **Ein Abschnitt und keine Karte.** Sie ist der letzte Abschnitt der
 * Revisionsansicht und steht in derselben vollflächigen Form wie die anderen —
 * eine Trennlinie darüber, kein Rahmen darum. Als Karte unter lauter
 * randlosen Abschnitten sah sie aus wie etwas, das nicht dazugehört.
 *
 * **Keine eigene Werkzeugleiste** (#136): Die Revisionsansicht führt schon ihre
 * eine (#41). „Aufklappen" wirkt auf diesen Abschnitt und ist zugleich der
 * einzige Weg zu seinem Inhalt — genau der Knopf, der laut
 * `docs/human/2026-08-27-werkzeugleiste-und-sektionen.md` immer sichtbar bleiben
 * muss und deshalb im Kopf der Sektion (`headerRight`) steht, nicht im Hover
 * ihrer Knöpfe.
 */
export function ComparisonPanel({
  client,
  revisionId,
  role,
  defaultOpen = false,
}: {
  client: ComparisonApi;
  revisionId: number;
  /** Die Rolle entscheidet allein über die Anzeige des Nachvollzugs — nicht über den Zugriff. */
  role: Role;
  /**
   * Steht sie beim ersten Aufbau offen? Unmittelbar nach der Abgabe ja — dann
   * ist sie das, was der Anwender gerade freigeschaltet hat.
   */
  defaultOpen?: boolean;
}): ReactElement {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <ListSection
      title="Gegenüberstellung"
      glyph="columns-2"
      headerRight={
        <Button
          variant="outline"
          size="sm"
          icon={open ? "chevron-down" : "chevron-right"}
          onClick={() => setOpen((previous) => !previous)}
        >
          {open ? "Einklappen" : "Aufklappen"}
        </Button>
      }
    >
      <div style={{ padding: "0 var(--sp-5) var(--sp-6)" }}>
        {open ? (
          <Comparison client={client} revisionId={revisionId} role={role} />
        ) : (
          <p
            style={{
              margin: 0,
              maxWidth: "78ch",
              fontSize: "var(--fs-11)",
              lineHeight: "var(--lh-normal)",
              color: "var(--fg-6)",
              textWrap: "pretty",
            }}
          >
            {FOLDED}
          </p>
        )}
      </div>
    </ListSection>
  );
}

/** Der Inhalt der Gegenüberstellung — er lädt, sobald es ihn gibt. */
function Comparison({
  client,
  revisionId,
  role,
}: {
  client: ComparisonApi;
  revisionId: number;
  role: Role;
}): ReactElement {
  const { state, reload } = useLoaded(() => client.comparison(revisionId));
  const [traced, setTraced] = useState<Finding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Die eine Rückfrage: Ein fremdes „behoben" würde überschrieben. Sie hält den
  // Handgriff fest, der abgewiesen wurde — bestätigt wird derselbe und kein neuer.
  const [overwriting, setOverwriting] = useState<Overwrite | null>(null);
  // Der Befund, zu dem gerade eine Begründung geschrieben wird — `null` heißt:
  // Das Formular steht nicht offen.
  const [followingUp, setFollowingUp] = useState<Finding | null>(null);

  const comparison = state.kind === "loaded" ? state.value : null;
  const followUps = comparison?.followUpMarks ?? [];

  /**
   * **Nachhalten** an einem Befund dieser Fläche (ADR-0012).
   *
   * Sie ist die zweite Stelle, an der ein Mensch einen Befund vor sich hat, und
   * die einzige, an der zwei Menschen denselben Agentenbefund sehen — der Knopf
   * gehört deshalb auch hierher und nicht nur in den Befundverlauf.
   */
  const followUp = (run: Promise<void>): void => {
    setBusy(true);
    setError(null);

    run.then(
      () => {
        setBusy(false);
        reload();
      },
      (failure: unknown) => {
        setBusy(false);
        setError(describe(failure));
      },
    );
  };

  /**
   * Die Stelle **bewerten** (ADR-0022) — derselbe Weg wie in der Detailansicht.
   *
   * Die Rückfrage steht nicht als roter Satz in der Fläche: Sie ist kein Fehler,
   * den man hinschreibt, sondern eine Frage, die man stellt. Als Fehler gelesen
   * hätte der Anwender erfahren, dass etwas schiefging, und keinen Weg, es
   * trotzdem zu tun.
   */
  const decide = (anchor: SiteAnchor, verdict: Verdict, force: boolean): void => {
    setBusy(true);
    setError(null);

    client.setSiteVerdict(anchor, verdict, force).then(
      () => {
        setBusy(false);
        setOverwriting(null);
        reload();
      },
      (failure: unknown) => {
        setBusy(false);

        if (failure instanceof ForeignFixError) {
          setOverwriting({ anchor, verdict, refused: failure });

          return;
        }

        setOverwriting(null);
        setError(describe(failure));
      },
    );
  };

  /** Die Rücknahme: leer und offen, an allen Befunden der Stelle. */
  const withdraw = (anchor: SiteAnchor): void => {
    setBusy(true);
    setError(null);

    client.withdrawSiteVerdict(anchor).then(
      () => {
        setBusy(false);
        reload();
      },
      (failure: unknown) => {
        setBusy(false);
        setError(describe(failure));
      },
    );
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {comparison?.submittedAt == null ? null : (
          <span
            style={{
              paddingBottom: "var(--sp-4)",
              fontSize: "var(--fs-11)",
              color: "var(--fg-6)",
            }}
          >
            {`Abgegeben am ${formatTimestamp(comparison.submittedAt)}`}
          </span>
        )}

        {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
        {state.kind === "failed" ? (
          <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}
        {error === null ? null : (
          <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
        )}

        {comparison !== null && !comparison.submitted ? (
          <Notice text={CLOSED} glyph="lock" />
        ) : null}
        {comparison !== null && comparison.submitted && comparison.agents.length === 0 ? (
          <Notice text={NOTHING} />
        ) : null}

        {comparison === null || !comparison.submitted
          ? null
          : comparison.agents.map((agent) => (
              <AgentSection
                key={agent.agentId}
                agent={agent}
                traceable={role === "admin"}
                busy={busy}
                followUps={followUps}
                onFollowUp={setFollowingUp}
                onWithdrawFollowUp={(found) => followUp(client.withdrawFollowUpMark(found.id))}
                onDecide={decide}
                onWithdraw={withdraw}
                onTrace={setTraced}
              />
            ))}

        {comparison === null || !comparison.submitted ? null : (
          <ControlSection
            control={comparison.control}
            busy={busy}
            followUps={followUps}
            onFollowUp={setFollowingUp}
            onWithdrawFollowUp={(found) => followUp(client.withdrawFollowUpMark(found.id))}
            onDecide={decide}
            onWithdraw={withdraw}
          />
        )}
      </div>

      {traced === null ? null : (
        <ModelCallDialog
          client={client}
          finding={traced}
          onClose={() => setTraced(null)}
        />
      )}

      {overwriting === null ? null : (
        <OverwriteDialog
          overwriting={overwriting}
          busy={busy}
          onClose={() => setOverwriting(null)}
          onConfirm={() => decide(overwriting.anchor, overwriting.verdict, true)}
        />
      )}

      {followingUp === null ? null : (
        <FollowUpDialog
          title={describeAnchor(anchorOfFinding(followingUp))}
          busy={busy}
          onClose={() => setFollowingUp(null)}
          onSave={(reason) => {
            const found = followingUp;
            setFollowingUp(null);

            followUp(client.addFollowUpMark(found.id, reason));
          }}
        />
      )}
    </>
  );
}

/**
 * Die Gegenüberstellung eines Agenten.
 *
 * Der Kopf zählt, was der Vergleich ergeben hat — Übereinstimmung, Übersehenes,
 * einseitig Maschinelles. Das ist dieselbe Aufteilung, aus der die
 * Abweichungskennzahl rechnet; wer sie hier liest, liest sie dort wieder.
 */
function AgentSection({
  agent,
  traceable,
  busy,
  followUps,
  onFollowUp,
  onWithdrawFollowUp,
  onDecide,
  onWithdraw,
  onTrace,
}: {
  agent: ComparisonAgent;
  traceable: boolean;
  busy: boolean;
  /** Der **Vermerkstand** aller Befunde dieser Antwort (Glossar, „Nachhalten"). */
  followUps: FollowUpTally[];
  onFollowUp: (finding: Finding) => void;
  onWithdrawFollowUp: (finding: Finding) => void;
  onDecide: (anchor: SiteAnchor, verdict: Verdict, force: boolean) => void;
  onWithdraw: (anchor: SiteAnchor) => void;
  onTrace: (finding: Finding) => void;
}): ReactElement {
  const matched = agent.matches.filter((one) => isMatched(one)).length;
  const missed = agent.matches.filter((one) => one.machineFinding === null).length;
  const unmatched = agent.matches.filter((one) => one.humanFinding === null).length;

  return (
    <section
      aria-label={`Gegenüberstellung mit ${agent.agentName}`}
      style={{ paddingTop: "var(--sp-5)" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--sp-4)",
          paddingBottom: "var(--sp-4)",
          borderBottom: "1px solid var(--line-f)",
        }}
      >
        <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-1)" }}>
          {agent.agentName}
        </span>
        <StatusPill tone="green">{`${formatNumber(matched)} übereinstimmend`}</StatusPill>
        <StatusPill tone="amber">{`${formatNumber(missed)} übersehen`}</StatusPill>
        <StatusPill tone="blue">{`${formatNumber(unmatched)} nur vom Agenten`}</StatusPill>
      </div>

      {agent.matches.length === 0 ? <Notice text={AGREED} glyph="check" /> : null}

      {agent.matches.map((match) => (
        <MatchRow
          key={match.id}
          match={match}
          traceable={traceable}
          busy={busy}
          followUps={followUps}
          onFollowUp={onFollowUp}
          onWithdrawFollowUp={onWithdrawFollowUp}
          onDecide={onDecide}
          onWithdraw={onWithdraw}
          onTrace={onTrace}
        />
      ))}
    </section>
  );
}

/**
 * Die dritte Spalte: die Befunde der **Kontrolle** (ADR-0010).
 *
 * Sie steht neben den Agenten und nicht in einem von ihnen — ein Kontrollbefund
 * gehört zu keinem, und ihn in einer Agentenreihe zu zeigen legte nahe, er ginge
 * in dessen Kennzahl ein. Der Kopf zählt deshalb auch keine Abweichung, sondern
 * die **Deckung**: wie viel die deterministische Quelle beigetragen hat und wie
 * oft ein Mensch dieselbe Stelle ebenfalls aufgeschrieben hat.
 *
 * **Übersehenes gibt es hier nicht.** Was kein Muster deckt, ist keine Abweichung
 * der Kontrolle; eine Zeile „von der Kontrolle übersehen" wäre eine Messung
 * genau der Quelle, die nicht gemessen wird.
 */
function ControlSection({
  control,
  busy,
  followUps,
  onFollowUp,
  onWithdrawFollowUp,
  onDecide,
  onWithdraw,
}: {
  control: ControlComparison;
  busy: boolean;
  followUps: FollowUpTally[];
  onFollowUp: (finding: Finding) => void;
  onWithdrawFollowUp: (finding: Finding) => void;
  onDecide: (anchor: SiteAnchor, verdict: Verdict, force: boolean) => void;
  onWithdraw: (anchor: SiteAnchor) => void;
}): ReactElement {
  return (
    <section
      aria-label="Befunde der Kontrolle"
      style={{ paddingTop: "var(--sp-5)" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--sp-4)",
          paddingBottom: "var(--sp-4)",
          borderBottom: "1px solid var(--line-f)",
        }}
      >
        <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-1)" }}>Kontrolle</span>
        <StatusPill tone="blue">{`${formatNumber(control.findings)} Kontrollbefunde`}</StatusPill>
        <StatusPill tone="green">{`${formatNumber(control.matched)} auch von Ihnen`}</StatusPill>
        {control.coverage === null ? null : (
          <Chip>{`Deckung ${formatShare(control.coverage)}`}</Chip>
        )}
      </div>

      {control.matches.length === 0 ? <Notice text={NO_CONTROL} glyph="check" /> : null}

      {control.matches.map((match) => (
        <ControlRow
          key={match.id}
          match={match}
          busy={busy}
          followUps={followUps}
          onFollowUp={onFollowUp}
          onWithdrawFollowUp={onWithdrawFollowUp}
          onDecide={onDecide}
          onWithdraw={onWithdraw}
        />
      ))}
    </section>
  );
}

/**
 * Ein Kontrollbefund neben der eigenen Aussage.
 *
 * Das Urteil in der Kopfzeile sagt, wer dieselbe Stelle gesehen hat, und nicht,
 * wer sie verfehlt hat: Die Kontrolle steht in keinem Vergleich, den sie
 * verlieren könnte.
 */
function ControlRow({
  match,
  busy,
  followUps,
  onFollowUp,
  onWithdrawFollowUp,
  onDecide,
  onWithdraw,
}: {
  match: FindingMatch;
  busy: boolean;
  followUps: FollowUpTally[];
  onFollowUp: (finding: Finding) => void;
  onWithdrawFollowUp: (finding: Finding) => void;
  onDecide: (anchor: SiteAnchor, verdict: Verdict, force: boolean) => void;
  onWithdraw: (anchor: SiteAnchor) => void;
}): ReactElement {
  const control = match.machineFinding;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-4)",
        padding: "var(--sp-5) 0",
        borderBottom: "1px solid var(--line-f)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "var(--sp-4)" }}>
        <StatusPill tone={match.humanFinding === null ? "blue" : "green"}>
          {match.humanFinding === null ? "Nur von der Kontrolle" : "Auch von Ihnen gefunden"}
        </StatusPill>

        {match.kindMatches === null ? null : (
          <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
            {match.kindMatches ? "gleiche Befundart" : "andere Befundart"}
          </span>
        )}
        {match.severityMatches === null ? null : (
          <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
            {match.severityMatches ? "gleicher Schweregrad" : "anderer Schweregrad"}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-5)" }}>
        <Side
          label="Ihr Befund"
          finding={match.humanFinding}
          followUp={followUpFor(match.humanFinding, followUps, busy, onFollowUp, onWithdrawFollowUp)}
        />

        {/* Kein Modellaufruf: Ein Kontrollbefund entsteht ohne einen. Und kein
            Nachhalten: Er *ist* schon ein Muster (ADR-0012). */}
        <Side label="Befund der Kontrolle" finding={control} />
      </div>

      <Handles match={match} busy={busy} onDecide={onDecide} onWithdraw={onWithdraw} />
    </div>
  );
}

/**
 * Eine Zuordnung, zweispaltig: links die eigene Aussage, rechts die des Agenten.
 *
 * Die leere Seite bleibt als Spalte stehen und sagt, dass dort nichts steht.
 * Zusammengeschoben sähen Übersehenes und einseitig Maschinelles gleich aus.
 */
function MatchRow({
  match,
  traceable,
  busy,
  followUps,
  onFollowUp,
  onWithdrawFollowUp,
  onDecide,
  onWithdraw,
  onTrace,
}: {
  match: FindingMatch;
  traceable: boolean;
  busy: boolean;
  followUps: FollowUpTally[];
  onFollowUp: (finding: Finding) => void;
  onWithdrawFollowUp: (finding: Finding) => void;
  onDecide: (anchor: SiteAnchor, verdict: Verdict, force: boolean) => void;
  onWithdraw: (anchor: SiteAnchor) => void;
  onTrace: (finding: Finding) => void;
}): ReactElement {
  const machine = match.machineFinding;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-4)",
        padding: "var(--sp-5) 0",
        borderBottom: "1px solid var(--line-f)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "var(--sp-4)" }}>
        <StatusPill tone={isMatched(match) ? "green" : "amber"}>{verdictOf(match)}</StatusPill>

        {/* Befundart und Schweregrad gehen nicht in den Treffer ein — sie stehen
            deshalb als eigene Aussage daneben und nicht im Urteil. */}
        {match.kindMatches === null ? null : (
          <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
            {match.kindMatches ? "gleiche Befundart" : "andere Befundart"}
          </span>
        )}
        {match.severityMatches === null ? null : (
          <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
            {match.severityMatches ? "gleicher Schweregrad" : "anderer Schweregrad"}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-5)" }}>
        <Side
          label="Ihr Befund"
          finding={match.humanFinding}
          followUp={followUpFor(match.humanFinding, followUps, busy, onFollowUp, onWithdrawFollowUp)}
        />

        <Side
          label="Befund des Agenten"
          finding={machine}
          followUp={followUpFor(machine, followUps, busy, onFollowUp, onWithdrawFollowUp)}
        >
          {machine === null || !traceable ? null : (
            <Button variant="outline" size="sm" onClick={() => onTrace(machine)}>
              Modellaufruf
            </Button>
          )}
        </Side>
      </div>

      <Handles match={match} busy={busy} onDecide={onDecide} onWithdraw={onWithdraw} />
    </div>
  );
}

/**
 * Die Handgriffe einer Zuordnung — **einer je Anker** (ADR-0022).
 *
 * Zugeordnet wird über **Überlappung** (`FindingMatcher`, ADR-0015): Dort geht
 * es darum, ob Mensch und Maschine dasselbe gesehen haben, und dafür ist ein
 * Zeilenversatz belanglos. Der Handgriff greift trotzdem am **exakten Anker**,
 * wie überall sonst — und wo eine Zuordnung über zwei Anker geht, stehen zwei
 * Handgriffe, von denen jeder nur seinen trifft. **Eine Gruppe ist keine Stelle**:
 * Ein Handgriff für beide setzte an Zeilen, die niemand angesehen hat.
 *
 * Sie stehen **unter** den zwei Spalten und in keiner von ihnen: Der Handgriff gilt
 * für alle Befunde seines Ankers, und in einer Spalte gelesen sähe er aus wie
 * ein Urteil über genau diesen einen Melder. Der Anker steht dabei, weil sonst
 * bei zwei Handgriffen nicht zu sagen wäre, welcher wohin greift.
 */
function Handles({
  match,
  busy,
  onDecide,
  onWithdraw,
}: {
  match: FindingMatch;
  busy: boolean;
  onDecide: (anchor: SiteAnchor, verdict: Verdict, force: boolean) => void;
  onWithdraw: (anchor: SiteAnchor) => void;
}): ReactElement | null {
  const sites = sitesOfMatch(match);

  if (sites.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
      {sites.map((site) => (
        <div
          key={site.key}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-3)",
            paddingTop: "var(--sp-4)",
            borderTop: "1px solid var(--line-f)",
          }}
        >
          <span
            style={{
              fontSize: "var(--fs-11)",
              color: "var(--fg-6)",
              fontVariantNumeric: "tabular-nums",
              overflowWrap: "anywhere",
            }}
          >
            {site.label}
          </span>

          <VerdictBar
            found={site.found}
            busy={busy}
            onDecide={(verdict) => onDecide(site.anchor, verdict, false)}
            onWithdraw={() => onWithdraw(site.anchor)}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Die **Stellen** einer Zuordnung: ihre Befunde, gruppiert nach dem exakten
 * Anker (ADR-0015).
 *
 * Meist ist es eine — Mensch und Maschine haben dieselben Zeilen genannt. Sind
 * es zwei, überlappen sie sich nur, und dann sind es zwei Stellen mit zwei
 * Urteilen. Die Reihenfolge ist die der Spalten darüber: erst die eigene Seite,
 * dann die der Maschine.
 */
function sitesOfMatch(match: FindingMatch): MatchedSite[] {
  const sites = new Map<string, MatchedSite>();

  for (const found of [match.humanFinding, match.machineFinding]) {
    if (found === null) {
      continue;
    }

    const key = siteKeyOf(found);
    const known = sites.get(key);

    if (known === undefined) {
      sites.set(key, {
        key,
        anchor: siteAnchorOf(found),
        label: describeAnchor(anchorOfFinding(found)),
        found: [found],
      });

      continue;
    }

    known.found.push(found);
  }

  return [...sites.values()];
}

/** Eine Stelle innerhalb einer Zuordnung: ihr Anker und die Befunde darunter. */
interface MatchedSite {
  key: string;
  anchor: SiteAnchor;
  /** Der Ort, ausgeschrieben — dieselbe Zeile wie im Kopf einer Stelle. */
  label: string;
  found: Finding[];
}

/** Eine Spalte der Zuordnung — mit Befund oder mit der Auskunft, dass keiner da ist. */
/**
 * Die Merkmale eines Befundes als Reihe von Pillen: Art, Schwere, Stand und —
 * wo es eines gibt — das Urteil.
 *
 * **Zwei Bausteine und nicht einer** (#141): Was eine Farbe trägt, ist ein
 * Zustand und steht als `StatusPill` da; was farblos bleibt — „hinweis",
 * „offen", „zur Kenntnis" —, ist ein Etikett und steht als `Chip` da. Welches
 * von beidem, sagt die Tonkonstante mit `null`, und nicht diese Stelle.
 *
 * Beide Achsen nebeneinander (Spec v1.4, Story 253): „bestätigt und offen" ist
 * der Normalfall, und eine Anzeige, die nur eine der beiden Angaben trüge,
 * verdeckte die andere.
 */
function FindingPills({ finding }: { finding: Finding }): ReactElement {
  const severity = SEVERITY_TONES[finding.severity];
  const status = STATUS_TONES[finding.status];
  const qualification = finding.qualification === null ? null : QUALIFICATION_TONES[finding.qualification];

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-3)" }}>
      <StatusPill tone="blue">{finding.kind}</StatusPill>

      {severity === null ? (
        <Chip>{finding.severity}</Chip>
      ) : (
        <StatusPill tone={severity}>{finding.severity}</StatusPill>
      )}

      {status === null ? (
        <Chip>{STATUS_LABELS[finding.status]}</Chip>
      ) : (
        <StatusPill tone={status}>{STATUS_LABELS[finding.status]}</StatusPill>
      )}

      {finding.qualification === null ? null : qualification === null ? (
        <Chip>{QUALIFICATION_LABELS[finding.qualification]}</Chip>
      ) : (
        <StatusPill tone={qualification}>{QUALIFICATION_LABELS[finding.qualification]}</StatusPill>
      )}
    </div>
  );
}

function Side({
  label,
  finding,
  followUp = null,
  children,
}: {
  label: string;
  finding: Finding | null;
  /**
   * Der Knopf zum **Nachhalten**, wo es einen gibt. Er kommt von außen und wird
   * hier nicht entschieden: Ob an einem Befund nachgehalten werden kann, hängt
   * an seiner Herkunft, und die kennt die Zeile darüber — diese Spalte bekommt
   * einen `Finding` ohne sie.
   */
  followUp?: ReactElement | null;
  children?: ReactElement | null;
}): ReactElement {
  const judged = finding === null ? null : labelOfJudgement(finding.qualifiedBy, finding.qualifiedAt);

  return (
    <div
      aria-label={label}
      role="group"
      style={{
        // Zwei Spalten, solange die Karte breit genug ist, darunter untereinander.
        flex: 1,
        minWidth: 300,
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-3)",
        padding: "var(--sp-4)",
        borderRadius: "var(--r-card)",
        background: finding === null ? "transparent" : "var(--bg-sunken)",
        border: "1px solid var(--line-f)",
      }}
    >
      <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>{label}</span>

      {finding === null ? (
        <span style={{ fontSize: "var(--fs-12)", color: "var(--text-meta)" }}>
          Hier steht nichts.
        </span>
      ) : (
        <>
          <span
            style={{
              fontSize: "var(--fs-11)",
              color: "var(--fg-1)",
              fontVariantNumeric: "tabular-nums",
              overflowWrap: "anywhere",
            }}
          >
            {describeAnchor(anchorOfFinding(finding))}
          </span>

          <FindingPills finding={finding} />

          {followUp}

          {/* Wer wann bewertet hat (Spec v1.4, Story 257). Die Gegenüberstellung
              ist eine Fläche unter mehreren, auf denen bewertet wird — das
              Urteil gilt für alle und trägt hier wie überall seinen Urheber
              (ADR-0022). */}
          {judged === null ? null : (
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>{judged}</span>
          )}

          <span
            style={{
              fontSize: "var(--fs-12)",
              lineHeight: "var(--lh-normal)",
              color: "var(--text-body)",
              textWrap: "pretty",
            }}
          >
            {finding.body}
          </span>
        </>
      )}

      {children}
    </div>
  );
}

/**
 * Der Nachvollzug: der Aufruf hinter einem maschinellen Befund, samt Agent,
 * Preset und **aufgelöstem Basismodell** (ADR-0005).
 *
 * Anfrage und Rohantwort stehen ungekürzt darin — wer hier nachsieht, sucht genau
 * die Stelle, die eine Kürzung fortnähme.
 */
function ModelCallDialog({
  client,
  finding,
  onClose,
}: {
  client: ModelCallApi;
  finding: Finding;
  onClose: () => void;
}): ReactElement {
  const { state } = useLoaded(() => client.modelCall(finding.id));
  const call = state.kind === "loaded" ? state.value : null;

  return (
    <Modal onClose={onClose}>
      <Dialog
        title="Modellaufruf"
        description={`Der Aufruf, aus dem dieser Befund hervorging: ${finding.path}`}
        width={860}
        onClose={onClose}
        footer={
          <Button variant="outline" size="lg" onClick={onClose}>
            Schließen
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}

          {call === null ? null : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-5)" }}>
                <Fact label="Agent" value={call.agentName} />
                <Fact label="Endpunkt" value={call.endpointName} />
                <Fact label="Preset" value={call.presetIdentifier} />
                <Fact label="Aufgelöstes Basismodell" value={call.resolvedBaseModel} />
                <Fact
                  label="Dauer"
                  value={call.durationMs === null ? "—" : `${formatNumber(call.durationMs)} ms`}
                />
                <Fact
                  label="Token"
                  value={
                    call.promptTokens === null && call.completionTokens === null
                      ? "—"
                      : `${formatNumber(call.promptTokens ?? 0)} / ${formatNumber(call.completionTokens ?? 0)}`
                  }
                />
              </div>

              <Wire label="Anfrage" text={call.request} />
              <Wire
                label="Rohantwort"
                text={call.rawResponse ?? "Es kam keine Antwort — Zeitüberschreitung oder Abbruch."}
              />
            </>
          )}
        </div>
      </Dialog>
    </Modal>
  );
}

function Fact({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>{label}</span>
      <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-1)" }}>{value}</span>
    </div>
  );
}

/** Was über den Draht ging, unverändert. */
function Wire({ label, text }: { label: string; text: string }): ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>{label}</span>
      <div
        aria-label={label}
        className="erp-scroll"
        style={{
          maxHeight: 220,
          overflow: "auto",
          padding: "var(--sp-4)",
          borderRadius: "var(--r-field)",
          background: "var(--bg-sunken)",
          border: "1px solid var(--line-f)",
          color: "var(--text-body)",
          fontSize: "var(--fs-11)",
          lineHeight: "var(--lh-normal)",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {text}
      </div>
    </div>
  );
}

/**
 * Der Knopf zum **Nachhalten** an einer Seite der Zuordnung — oder nichts, wo
 * keine Seite steht.
 *
 * An einer Stelle, weil vier Spalten ihn brauchen und drei ihn bekommen: Die
 * vierte ist der Kontrollbefund, und dort wird er ausdrücklich nicht angeboten
 * (ADR-0012). Der Unterschied steht in der Zeile darüber, wo die Herkunft
 * bekannt ist.
 */
function followUpFor(
  finding: Finding | null,
  followUps: FollowUpTally[],
  busy: boolean,
  onFollowUp: (finding: Finding) => void,
  onWithdrawFollowUp: (finding: Finding) => void,
): ReactElement | null {
  if (finding === null) {
    return null;
  }

  return (
    <FollowUp
      tally={followUpOf(followUps, finding.id)}
      busy={busy}
      onMark={() => onFollowUp(finding)}
      onWithdraw={() => onWithdrawFollowUp(finding)}
    />
  );
}

function isMatched(match: FindingMatch): boolean {
  return match.humanFinding !== null && match.machineFinding !== null;
}

/** Das Urteil einer Zuordnung, in einem Wort. */
function verdictOf(match: FindingMatch): string {
  if (isMatched(match)) {
    return "Übereinstimmung";
  }

  return match.machineFinding === null ? "Vom Agenten übersehen" : "Nur vom Agenten";
}
